import React, { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import TextInput from "shared/@forms/TextInput";
import Input from "shared/@forms/Input";
import { Button } from "shared/Button";
import { ChevronDown } from "react-ikonate";
import styles from "./FileUploadSection.module.css";

const DEFAULT_FILE_TYPE_OPTIONS = ["Video", "Audio"];

const getSaveLabel = (fileType) => {
  if (!fileType) {
    return "Save";
  }

  return `Save ${fileType}`;
};

export function FileUploadSectionV1({
  label,
  onSave,
  onCancel,
  fileTypeOptions,
  defaultFileType,
  defaultId,
  defaultVersion,
  onFileTypeChange,
}) {
  const [fileType, setFileType] = useState(defaultFileType);
  const [idValue, setIdValue] = useState(defaultId);
  const [version, setVersion] = useState(defaultVersion);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  const hasFileType = !!fileType;
  const trimmedId = idValue.trim();
  const isSaveDisabled = !hasFileType || !trimmedId;

  const saveLabel = useMemo(() => getSaveLabel(fileType), [fileType]);

  // Update file type when defaultFileType changes (e.g., when Media Type changes)
  useEffect(() => {
    setFileType(defaultFileType);
  }, [defaultFileType]);

  useEffect(() => {
    const onDocumentMouseDown = (event) => {
      if (!dropdownRef.current?.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", onDocumentMouseDown);
    return () => {
      document.removeEventListener("mousedown", onDocumentMouseDown);
    };
  }, []);

  const resetForm = () => {
    setFileType(defaultFileType);
    setIdValue(defaultId);
    setVersion(defaultVersion);
  };

  const handleSave = () => {
    if (isSaveDisabled) return;

    onSave({
      fileType,
      id: trimmedId,
      version: Number(version),
    });

    resetForm();
  };

  const handleCancel = () => {
    resetForm();
    onCancel();
  };

  const handleVersionChange = (event) => {
    const nextValue = parseInt(event.target.value, 10);

    if (Number.isNaN(nextValue)) {
      setVersion(defaultVersion);
      return;
    }

    setVersion(nextValue < 1 ? 1 : nextValue);
  };

  return (
    <section className={styles.container}>
      <div className={styles.headerRow}>
        <h3 className={styles.heading}>{label}</h3>
        <div className={styles.dropdownContainer} ref={dropdownRef}>
          <button
            type="button"
            className={`${styles.fileTypeSelect} ${
              isDropdownOpen ? styles.fileTypeSelectOpen : ""
            }`}
            onClick={() => setIsDropdownOpen((prev) => !prev)}
            aria-haspopup="listbox"
            aria-expanded={isDropdownOpen}
          >
            <span
              className={
                hasFileType ? styles.selectValue : styles.selectPlaceholder
              }
            >
              {fileType || "Select File Type"}
            </span>
            <span
              className={`${styles.selectChevron} ${
                isDropdownOpen ? styles.selectChevronOpen : ""
              }`}
            >
              <ChevronDown width={20} height={20} />
            </span>
          </button>

          {isDropdownOpen && (
            <div className={styles.dropdownMenu} role="listbox">
              {fileTypeOptions.map((option) => (
                <button
                  type="button"
                  key={option}
                  className={styles.dropdownOption}
                  onClick={() => {
                    setFileType(option);
                    setIsDropdownOpen(false);
                    // Notify parent when file type changes so Media Type can be updated
                    if (onFileTypeChange) {
                      onFileTypeChange(option);
                    }
                  }}
                >
                  {option}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className={styles.headerDivider} />

      {!hasFileType ? (
        <p className={styles.instructions}>Choose a File Type</p>
      ) : (
        <div className={styles.expandedContent}>
          <div className={styles.inputRow}>
            <TextInput
              className={styles.idInput}
              value={idValue}
              onChange={(event) => setIdValue(event.target.value)}
              placeholder="Enter ID"
              aria-label="File ID"
            />

            <div className={styles.versionGroup}>
              <span className={styles.versionLabel}>Version:</span>
              <Input
                type="number"
                min={1}
                step={1}
                className={styles.versionInput}
                value={version}
                onChange={handleVersionChange}
                aria-label="Version"
              />
            </div>
          </div>

          <div className={styles.actionRow}>
            <Button className={styles.cancelButton} onClick={handleCancel}>
              Cancel
            </Button>
            <Button
              className={styles.saveButton}
              onClick={handleSave}
              disabled={isSaveDisabled}
            >
              {saveLabel}
            </Button>
          </div>
        </div>
      )}
    </section>
  );
}

FileUploadSectionV1.propTypes = {
  label: PropTypes.string,
  onSave: PropTypes.func,
  onCancel: PropTypes.func,
  fileTypeOptions: PropTypes.arrayOf(PropTypes.string),
  defaultFileType: PropTypes.string,
  defaultId: PropTypes.string,
  defaultVersion: PropTypes.number,
  onFileTypeChange: PropTypes.func,
};

FileUploadSectionV1.defaultProps = {
  label: "Add New File",
  onSave: () => {},
  onCancel: () => {},
  fileTypeOptions: DEFAULT_FILE_TYPE_OPTIONS,
  defaultFileType: "",
  defaultId: "",
  defaultVersion: 1,
  onFileTypeChange: () => {},
};

export default FileUploadSectionV1;
